/**
 * Axios instance.
 *
 * SINGLE source of truth for all real (non-mock) HTTP communication
 * with the backend. Configures:
 *   - Base URL from env
 *   - Cookies enabled (for refresh token)
 *   - Request interceptor: attach Bearer access token from Redux
 *   - Response interceptor: on 401 → call /auth/refresh → retry original
 *
 * For Person B (backend dev):
 *   - Expect "Authorization: Bearer <jwt>" on all protected routes
 *   - Expect cookies to be sent (Access-Control-Allow-Credentials: true)
 *   - 401 response on expired access token triggers automatic refresh
 *   - 401 response from /auth/refresh triggers full logout
 */

import axios from 'axios';
import { store } from '@/store';
import { setAccessToken, setRefreshing, logout } from '@/store/authSlice';

const BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

export const axiosInstance = axios.create({
  baseURL: BASE_URL,
  withCredentials: true, // CRITICAL: sends the refresh_token cookie
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// =====================================================================
// Request interceptor: attach access token
// =====================================================================
axiosInstance.interceptors.request.use(
  (config) => {
    const state = store.getState();
    const token = state.auth.accessToken;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// =====================================================================
// Response interceptor: auto-refresh on 401
// =====================================================================
let refreshPromise = null;

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Only handle 401 once per request, and never for the refresh endpoint itself
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/refresh') &&
      !originalRequest.url?.includes('/auth/verify-otp') &&
      !originalRequest.url?.includes('/auth/request-otp')
    ) {
      originalRequest._retry = true;

      try {
        // If a refresh is already in flight, wait for it
        if (!refreshPromise) {
          store.dispatch(setRefreshing(true));
          refreshPromise = axios
            .post(
              `${BASE_URL}/auth/refresh`,
              // Which console is asking. The backend keeps one session per
              // role, so this refreshes THIS app's session rather than
              // whichever was signed into last on a shared browser.
              { role: 'user' },
              { withCredentials: true }
            )
            .then((res) => {
              // Both shapes accepted: /auth/refresh envelopes its body while
              // its siblings do not, and reading only one yields `undefined`
              // — which then gets stored as the token and sends `Bearer
              // undefined` on the retry.
              const newToken = res.data?.accessToken ?? res.data?.data?.accessToken;
              if (!newToken) throw new Error('No access token in refresh response');
              store.dispatch(setAccessToken(newToken));
              return newToken;
            })
            .finally(() => {
              store.dispatch(setRefreshing(false));
              refreshPromise = null;
            });
        }

        const newToken = await refreshPromise;
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        // Only a definite 401 from the refresh endpoint means the session is
        // over. A timeout, a dropped connection or a proxy error means we do
        // not know, and signing the client out then — with a valid cookie
        // still in the browser — is avoidable re-login churn. The stored
        // token is left alone so the next request can try again.
        if (refreshError?.response?.status === 401) store.dispatch(logout());
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
